if (!customElements.get("facet-filters")) {
  class FacetFilters extends SideDrawer {
    connectedCallback() {
      this.init();
    }

    disconnectedCallback() {
      window.removeEventListener("popstate", this.historyChangeHandler);
      window.removeEventListener("on:breakpoint-change", this.breakpointChangeHandler);
      document.removeEventListener("click", this.documentClickHandler);
      document.removeEventListener("keydown", this.documentKeydownHandler);
      this.desktopSortBy?.removeEventListener("change", this.desktopSortChangeHandler);
    }

    init() {
      this.filteringEnabled = this.dataset.filtering === "true";
      this.sortingEnabled = this.dataset.sorting === "true";
      this.form = document.getElementById("facets");
      this.results = document.getElementById("filter-results");
      this.expanded = this.expanded || [];
      this.drawerStateSequence = this.drawerStateSequence || 0;

      this.addElements();
      this.handleBreakpointChange();
      this.addListeners();

      this.historyChangeHandler =
        this.historyChangeHandler || this.handleHistoryChange.bind(this);
      window.removeEventListener("popstate", this.historyChangeHandler);
      window.addEventListener("popstate", this.historyChangeHandler);
    }

    addElements() {
      if (this.filteringEnabled) {
        this.filters = this.querySelector(".facets__filters");
        this.activeFilters = this.querySelector(".facets__active-filters");
        this.activeFiltersList = this.querySelector(".active-filters");
        this.activeFiltersHeader = this.querySelector(".active-filters-header");
        this.footer = this.querySelector(".facets__footer");
      }

      if (this.sortingEnabled) {
        this.mobileSortByOptions = this.querySelectorAll(".js-drawer-sort-by");
        this.desktopSortBy = document.querySelector(".products-toolbar__sort");
      }
    }

    addListeners() {
      if (this.filteringEnabled && this.filters && this.activeFilters) {
        this.breakpointChangeHandler =
          this.breakpointChangeHandler || this.handleBreakpointChange.bind(this);
        this.filtersClickHandler =
          this.filtersClickHandler || this.handleFiltersClick.bind(this);
        this.filtersInputHandler =
          this.filtersInputHandler || debounce(this.handleFilterChange.bind(this), 500);
        this.filtersToggleHandler =
          this.filtersToggleHandler || this.handleFiltersToggle.bind(this);
        this.activeFiltersClickHandler =
          this.activeFiltersClickHandler || this.handleActiveFiltersClick.bind(this);
        this.documentClickHandler =
          this.documentClickHandler || this.handleDocumentClick.bind(this);
        this.documentKeydownHandler =
          this.documentKeydownHandler || this.handleDocumentKeydown.bind(this);

        this.filters.removeEventListener("click", this.filtersClickHandler);
        this.filters.addEventListener("click", this.filtersClickHandler);
        this.filters.removeEventListener("input", this.filtersInputHandler);
        this.filters.addEventListener("input", this.filtersInputHandler);
        this.filters.removeEventListener("toggle", this.filtersToggleHandler, true);
        this.filters.addEventListener("toggle", this.filtersToggleHandler, true);
        this.activeFilters.removeEventListener("click", this.activeFiltersClickHandler);
        this.activeFilters.addEventListener("click", this.activeFiltersClickHandler);

        window.removeEventListener("on:breakpoint-change", this.breakpointChangeHandler);
        window.addEventListener("on:breakpoint-change", this.breakpointChangeHandler);
        document.removeEventListener("click", this.documentClickHandler);
        document.addEventListener("click", this.documentClickHandler);
        document.removeEventListener("keydown", this.documentKeydownHandler);
        document.addEventListener("keydown", this.documentKeydownHandler);
      }

      if (this.sortingEnabled && this.desktopSortBy) {
        this.desktopSortChangeHandler =
          this.desktopSortChangeHandler || this.handleFilterChange.bind(this);
        this.desktopSortBy.removeEventListener("change", this.desktopSortChangeHandler);
        this.desktopSortBy.addEventListener("change", this.desktopSortChangeHandler);
      }
    }

    open(opener, focusTarget, callback) {
      const openSequence = ++this.drawerStateSequence;
      this.overlay?.classList.add("overlay--facet-filters");
      super.open(
        opener || document.querySelector(".js-toggle-filters"),
        focusTarget,
        () => {
          if (this.drawerStateSequence === openSequence) callback?.();
        }
      );
      this.syncToolbarState();
    }

    close(callback) {
      if (theme.mediaMatches.lg) {
        if (this.usesDesktopPopoverLayout()) this.closeDesktopDisclosures();
        return;
      }

      const closingSequence = ++this.drawerStateSequence;
      super.close(() => {
        if (
          this.drawerStateSequence !== closingSequence ||
          this.isMobileDrawerOpen()
        ) {
          return;
        }
        this.overlay?.classList.remove("overlay--facet-filters");
        this.syncToolbarState();
        callback?.();
      });
      this.syncToolbarState();
    }

    isMobileDrawerOpen() {
      return (
        this.getAttribute("role") === "dialog" &&
        this.hasAttribute("open") &&
        this.getAttribute("aria-hidden") === "false"
      );
    }

    releaseMobileDrawerLock() {
      this.drawerStateSequence += 1;
      const scrollY = Number.isFinite(this.scrollY) ? this.scrollY : window.scrollY;

      this.overlay?.classList.remove("is-visible");
      this.overlay?.classList.remove("overlay--facet-filters");
      removeTrapFocus();
      document.documentElement.style.height = "";
      document.body.style.top = "";
      document.body.classList.remove("fixed", "overflow-hidden");

      if (this.clickHandler) {
        this.removeEventListener("click", this.clickHandler);
        this.overlay?.removeEventListener("click", this.clickHandler);
      }
      if (this.keyupHandler) this.removeEventListener("keyup", this.keyupHandler);
      window.scrollTo(0, scrollY);
    }

    syncToolbarState() {
      const toggle = document.querySelector(".js-toggle-filters");
      const filtersColumn = document.querySelector(".main-products-grid__filters");
      if (!toggle) return;

      const expanded = theme.mediaMatches.lg
        ? Boolean(filtersColumn && !filtersColumn.classList.contains("lg:js-hidden"))
        : this.isMobileDrawerOpen();

      toggle.classList.toggle("is-active", expanded);
      toggle.setAttribute("aria-expanded", String(expanded));
    }

    handleBreakpointChange() {
      const wasMobileDrawerOpen = this.isMobileDrawerOpen();
      const focusWasInside = this.contains(document.activeElement);

      if (theme.mediaMatches.lg) {
        if (wasMobileDrawerOpen) this.releaseMobileDrawerLock();
        this.setAttribute("open", "");
        this.setAttribute("aria-hidden", "false");
        this.removeAttribute("aria-modal");
        this.removeAttribute("role");
      } else {
        this.setAttribute("role", "dialog");
        this.setAttribute("aria-modal", "true");
        this.hidden = false;

        if (!wasMobileDrawerOpen) {
          this.removeAttribute("open");
          this.setAttribute("aria-hidden", "true");
          this.overlay?.classList.remove("overlay--facet-filters");
        }
      }

      this.syncToolbarState();
      if (theme.mediaMatches.lg) {
        window.requestAnimationFrame(() => {
          if (this.usesDesktopPopoverLayout()) {
            this.querySelectorAll(".facets__filters details.filter[open]").forEach(
              (details) => this.fitDesktopDisclosure(details)
            );
          }
          if (focusWasInside && wasMobileDrawerOpen) {
            document
              .querySelector(".js-toggle-filters")
              ?.focus({ preventScroll: true });
          }
        });
      }
    }

    handleFilterChange(event) {
      const formData = new FormData(this.form);
      const searchParams = new URLSearchParams(formData);
      const emptyParams = [];

      if (this.sortingEnabled) {
        let sortBy = searchParams.get("sort_by");

        if (event.target.tagName === "CUSTOM-SELECT") {
          sortBy = event.detail.selectedValue;
          this.mobileSortByOptions.forEach((option) => {
            option.checked = option.value === sortBy;
          });
        }

        if (sortBy) searchParams.set("sort_by", sortBy);
      }

      searchParams.forEach((value, name) => {
        if (!value) emptyParams.push(name);
      });
      emptyParams.forEach((name) => searchParams.delete(name));

      this.applyFilters(searchParams.toString(), event);
    }

    handleFiltersClick(event) {
      const { target } = event;
      const toggle = target.closest(".filter__toggle");

      if (toggle && theme.mediaMatches.lg && this.usesDesktopPopoverLayout()) {
        const currentDetails = toggle.closest("details.filter");
        window.requestAnimationFrame(() => {
          if (currentDetails?.open) this.closeDesktopDisclosures(currentDetails);
        });
      }

      const clearFilter = target.closest(".js-clear-filter");
      if (clearFilter) {
        event.preventDefault();
        this.applyFilters(new URL(clearFilter.href).searchParams.toString(), event);
      }

      const showMore = target.closest(".js-show-more");
      if (showMore) {
        const filter = showMore.closest(".filter");
        showMore.remove();
        filter.querySelectorAll("li").forEach((item) => {
          item.classList.remove("js-hidden");
        });
        if (!this.expanded.includes(filter.id)) this.expanded.push(filter.id);
      }
    }

    handleFiltersToggle(event) {
      if (
        theme.mediaMatches.lg &&
        this.usesDesktopPopoverLayout() &&
        event.target.matches("details.filter") &&
        event.target.open
      ) {
        this.closeDesktopDisclosures(event.target);
        window.requestAnimationFrame(() => this.fitDesktopDisclosure(event.target));
      }
    }

    fitDesktopDisclosure(details) {
      if (!this.usesDesktopPopoverLayout()) return;

      const panel = details.querySelector(":scope > .disclosure__panel");
      const toggle = details.querySelector(":scope > summary");
      if (!panel || !toggle) return;

      panel.style.top = "calc(100% + 1px)";
      panel.style.bottom = "auto";
      panel.style.left = "0px";
      panel.style.right = "auto";

      const edgeGap = 16;
      const panelTop = panel.getBoundingClientRect().top;
      const availableBelow = Math.max(80, window.innerHeight - panelTop - edgeGap);
      const availableAbove = Math.max(80, toggle.getBoundingClientRect().top - edgeGap);

      if (availableBelow < 240 && availableAbove > availableBelow) {
        panel.style.top = "auto";
        panel.style.bottom = "calc(100% + 1px)";
        panel.style.maxHeight = `${Math.min(480, availableAbove)}px`;
      } else {
        panel.style.maxHeight = `${Math.min(480, availableBelow)}px`;
      }

      const horizontalGap = 16;
      const panelRect = panel.getBoundingClientRect();
      let horizontalOffset = 0;
      if (panelRect.left < horizontalGap) {
        horizontalOffset = horizontalGap - panelRect.left;
      } else if (panelRect.right > window.innerWidth - horizontalGap) {
        horizontalOffset = window.innerWidth - horizontalGap - panelRect.right;
      }
      panel.style.left = `${horizontalOffset}px`;
    }

    handleActiveFiltersClick(event) {
      const link = event.target.closest("a");
      if (!link) return;

      event.preventDefault();
      this.applyFilters(new URL(link.href).searchParams.toString(), event);
    }

    handleDocumentClick(event) {
      if (!theme.mediaMatches.lg || !this.usesDesktopPopoverLayout()) return;

      const toggle = event.target.closest(".filter__toggle");
      if (toggle && this.contains(toggle)) {
        const currentDetails = toggle.closest("details.filter");
        window.requestAnimationFrame(() => {
          if (currentDetails?.open) this.closeDesktopDisclosures(currentDetails);
        });
      } else if (!this.contains(event.target)) {
        this.closeDesktopDisclosures();
      }
    }

    handleDocumentKeydown(event) {
      if (
        !theme.mediaMatches.lg ||
        !this.usesDesktopPopoverLayout() ||
        event.key !== "Escape"
      ) {
        return;
      }

      const openFilter = this.querySelector(".facets__filters details.filter[open]");
      if (!openFilter) return;

      event.preventDefault();
      const toggle = openFilter.querySelector("summary");
      this.closeDisclosure(openFilter);
      toggle?.focus();
    }

    closeDisclosure(details) {
      details.open = false;
      details.classList.remove("is-closing");
      details.querySelector(":scope > .disclosure__panel")?.removeAttribute("style");
    }

    closeDesktopDisclosures(except = null) {
      this.querySelectorAll(".facets__filters details.filter[open]").forEach((details) => {
        if (details !== except) this.closeDisclosure(details);
      });
    }

    usesDesktopPopoverLayout() {
      return Boolean(this.closest(".main-products-grid--top-filters"));
    }

    getFocusReturnSelector(event) {
      const target = event?.target;
      if (!(target instanceof Element) || !this.contains(target)) return null;

      const details = target.closest("details[id]");
      return details ? `#${CSS.escape(details.id)} > summary` : null;
    }

    restoreFilterFocus(selector) {
      if (!selector) return;

      const isVisible = (element) =>
        Boolean(element && element.getClientRects().length && !element.closest("[hidden]"));
      let focusTarget = this.form.querySelector(selector);

      if (!isVisible(focusTarget)) {
        focusTarget = [...this.form.querySelectorAll(".facets__filters .filter__toggle")]
          .find(isVisible);
      }
      if (!isVisible(focusTarget) && !theme.mediaMatches.lg) {
        focusTarget = this.querySelector(".js-close-drawer");
      }

      focusTarget?.focus({ preventScroll: true });
    }

    handleHistoryChange(event) {
      if (event.state === null) return;

      const searchParams = event.state?.searchParams || "";
      this.applyFilters(searchParams, null, false);
    }

    async applyFilters(searchParams, _event, updateHistory = true) {
      const paginationComponent = document.querySelector("custom-pagination");
      const mobileResultsButton = this.querySelector(".js-close-drawer-mob");
      const focusReturnSelector = this.getFocusReturnSelector(_event);
      let requestController = null;

      try {
        if (paginationComponent) {
          paginationComponent.dataset.pauseInfiniteScroll = "true";
          paginationComponent.cancelLoadMore?.();
        }
        this.results.classList.add("is-loading");
        mobileResultsButton?.setAttribute("aria-disabled", "true");
        mobileResultsButton?.classList.add("is-loading");

        const requestUrl = new URL(window.location.pathname, window.location.origin);
        new URLSearchParams(searchParams).forEach((value, name) => {
          requestUrl.searchParams.append(name, value);
        });
        if (this.form.dataset.filterSectionId) {
          requestUrl.searchParams.set("section_id", this.form.dataset.filterSectionId);
        }

        this.applyFiltersFetchAbortController?.abort("Request changed");
        requestController = new AbortController();
        this.applyFiltersFetchAbortController = requestController;

        const response = await fetch(requestUrl.toString(), {
          method: "GET",
          signal: requestController.signal
        });
        if (!response.ok) throw new Error(`Filter request failed (${response.status})`);

        const template = document.createElement("template");
        template.innerHTML = await response.text();
        const incomingForm = template.content.getElementById("facets");
        const incomingResults = template.content.getElementById("filter-results");
        if (!incomingForm || !incomingResults) {
          throw new Error("Filter response did not include the expected collection section.");
        }

        this.updateCollectionResultCount(template.content);

        this.form.querySelectorAll("details-disclosure > details").forEach((details) => {
          const incomingDetails = template.content.getElementById(details.id);
          if (incomingDetails) incomingDetails.open = details.open;
        });

        template.content
          .querySelectorAll("#facets details-disclosure > details")
          .forEach((details) => {
            if (!this.expanded.includes(details.id)) return;
            details.querySelectorAll(".js-hidden").forEach((item) => {
              item.classList.remove("js-hidden");
            });
            details.querySelector(".filter__more")?.remove();
          });

        this.form.innerHTML = incomingForm.innerHTML;
        const incomingMobileButton = template.content.querySelector(
          ".js-close-drawer-mob"
        );
        if (mobileResultsButton && incomingMobileButton) {
          mobileResultsButton.innerText = incomingMobileButton.innerText;
        }

        const currentGrid = this.results.querySelector("ul");
        this.currentResultsClass = currentGrid
          ? currentGrid.getAttribute("class")
          : this.currentResultsClass;
        this.results.innerHTML = incomingResults.innerHTML;

        const incomingGrid = this.results.querySelector("ul");
        if (incomingGrid && this.currentResultsClass) {
          incomingGrid.setAttribute("class", this.currentResultsClass);
        }

        this.addElements();
        this.addListeners();
        window.requestAnimationFrame(() => {
          if (theme.mediaMatches.lg && this.usesDesktopPopoverLayout()) {
            this.querySelectorAll(".facets__filters details.filter[open]").forEach(
              (details) => this.fitDesktopDisclosure(details)
            );
          }
          this.restoreFilterFocus(focusReturnSelector);
        });
        if (typeof initLazyImages === "function") initLazyImages();
        paginationComponent?.reload?.();
        if (updateHistory) FacetFilters.updateURL(searchParams);

        if (this.results.getBoundingClientRect().top < 0) {
          let headerHeight = 0;
          if (document.querySelector('store-header[data-is-sticky="true"]')) {
            headerHeight =
              Number.parseInt(
                getComputedStyle(this.parentElement)
                  .getPropertyValue("--header-height")
                  .replace("px", ""),
                10
              ) || 0;
          }

          window.scrollTo({
            top:
              this.results.getBoundingClientRect().top +
              window.scrollY -
              headerHeight -
              45,
            behavior: window.matchMedia("(prefers-reduced-motion: reduce)").matches
              ? "auto"
              : "smooth"
          });
        }

        document.dispatchEvent(
          new CustomEvent("on:facet-filters:updated", { bubbles: true })
        );
      } catch (error) {
        if (error?.name !== "AbortError" && error !== "Request changed") {
          console.warn(error);
        }
      } finally {
        if (
          !requestController ||
          this.applyFiltersFetchAbortController === requestController
        ) {
          this.results.classList.remove("is-loading");
          mobileResultsButton?.classList.remove("is-loading");
          mobileResultsButton?.removeAttribute("aria-disabled");
          if (paginationComponent) {
            paginationComponent.dataset.pauseInfiniteScroll = "false";
          }
          this.applyFiltersFetchAbortController = null;
        }
      }
    }

    updateCollectionResultCount(content) {
      const resultMeta = content.querySelector("[data-num-results]");
      const bannerCount = document.querySelector("[data-collection-result-count]");
      const currentResultMeta = document.querySelector("[data-num-results]");

      if (currentResultMeta && resultMeta) {
        currentResultMeta.dataset.numResults = resultMeta.dataset.numResults;
        currentResultMeta.dataset.resultsLabel = resultMeta.dataset.resultsLabel;
      }
      if (bannerCount && resultMeta?.dataset.resultsLabel) {
        bannerCount.textContent = `(${resultMeta.dataset.resultsLabel})`;
      }
    }

    static updateURL(searchParams) {
      window.history.pushState(
        { searchParams },
        "",
        `${window.location.pathname}${searchParams ? `?${searchParams}` : ""}`
      );
    }
  }

  customElements.define("facet-filters", FacetFilters);
}
